import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  MediaUrlField,
  PeopleSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  enumOptionsAt,
  isRecord,
  peopleNames,
  str,
  threadKey,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  deleteAtPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

export function MessagesPanel({
  root,
  disabled,
  onChangeRoot,
  appPath = "device.apps.messages",
  title = "短信",
  supportsGroups = false,
  supportsImages = false,
}: PanelProps & {
  appPath?: string;
  title?: string;
  supportsGroups?: boolean;
  supportsImages?: boolean;
}) {
  const messages = asArray(getAtPath(root, appPath));
  const names = peopleNames(root);
  const [threadFilter, setThreadFilter] = useState<string>("all");
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(messages.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, `${appPath}[0]`);

  const threads = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of messages) {
      if (!isRecord(m)) continue;
      const key = messageThreadKey(m, supportsGroups);
      if (!key) continue;
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [messages, supportsGroups]);

  const visible = messages
    .map((m, index) => ({ m, index }))
    .filter(({ m }) => {
      if (threadFilter === "all") return true;
      if (!isRecord(m)) return false;
      return messageThreadKey(m, supportsGroups) === threadFilter;
    });

  return (
    <div className="flex flex-col gap-3">
      {threads.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          <Button
            type="button"
            size="xs"
            variant={threadFilter === "all" ? "secondary" : "ghost"}
            onClick={() => setThreadFilter("all")}
          >
            全部 ({messages.length})
          </Button>
          {threads.map(([key, n]) => (
            <Button
              key={key}
              type="button"
              size="xs"
              variant={threadFilter === key ? "secondary" : "ghost"}
              onClick={() => setThreadFilter(key)}
            >
              {threadLabel(key)} ({n})
            </Button>
          ))}
        </div>
      ) : null}

      <ArrayShell
        title={title}
        count={
          threadFilter === "all" ? messages.length : visible.length
        }
        disabled={disabled}
        onAdd={() => {
          const row = itemSchema
            ? emptyValueForSchema(itemSchema)
            : {
                sender: names[0] ?? "",
                receiver: names[1] ?? names[0] ?? "",
                sent_at: "",
                body: "",
              };
          if (threadFilter !== "all" && isRecord(row)) {
            const source = messages.find(
              (message) =>
                isRecord(message) &&
                messageThreadKey(message, supportsGroups) === threadFilter,
            );
            if (isRecord(source)) {
              row.sender = source.sender;
              row.receiver = source.receiver;
              if (supportsGroups && str(source.group_id)) {
                row.group_id = source.group_id;
                row.group_name = source.group_name;
                row.group_avatar_url = source.group_avatar_url;
                row.group_members = source.group_members;
              }
            }
          }
          onChangeRoot(appendAtPath(root, appPath, row));
          setOpenIndex(messages.length);
          setThreadFilter("all");
        }}
      >
        <ul className="flex flex-col gap-2">
          {visible.map(({ m, index }) => {
            const rec = isRecord(m) ? m : {};
            const itemPath = childPath(appPath, index);
            const groupName = str(rec.group_name);
            const isGroup =
              supportsGroups &&
              Boolean(
                str(rec.group_id) ||
                  groupName ||
                  str(rec.group_avatar_url) ||
                  asArray(rec.group_members).length,
              );
            const titleText =
              `${str(rec.sender)} → ${groupName || str(rec.receiver)}` +
              (isGroup ? "（群聊）" : "") +
              (str(rec.sent_at) ? ` · ${str(rec.sent_at)}` : "");
            const bodyPreview = str(rec.body);
            const contentPreview =
              bodyPreview ||
              (supportsImages && str(rec.image_url) ? "图片" : "");
            return (
              <RowCard
                key={itemPath}
                title={
                  contentPreview
                    ? `${titleText} · ${contentPreview.slice(0, 32)}${contentPreview.length > 32 ? "…" : ""}`
                    : titleText || `消息 ${index + 1}`
                }
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={messages.length}
                arrayPath={appPath}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                {supportsGroups ? (
                  <BoolField
                    label="群聊"
                    value={isGroup}
                    disabled={disabled}
                    onChange={(enabled) => {
                      const nextRec = { ...rec };
                      if (enabled) {
                        nextRec.group_id = "";
                        nextRec.group_name = "";
                        nextRec.group_avatar_url = "";
                        nextRec.group_members = names.slice(0, 3).map((name) => ({
                          name,
                          nickname: name,
                          avatar_url: "",
                        }));
                        nextRec.receiver = "";
                      } else {
                        delete nextRec.group_id;
                        delete nextRec.group_name;
                        delete nextRec.group_avatar_url;
                        delete nextRec.group_members;
                        nextRec.receiver = "";
                      }
                      onChangeRoot(setAtPath(root, itemPath, nextRec));
                    }}
                  />
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <PeopleSelect
                    label="发送方"
                    value={str(rec.sender)}
                    names={names}
                    disabled={disabled}
                    allowEmpty
                    onChange={(v) =>
                      onChangeRoot(
                        setAtPath(root, childPath(itemPath, "sender"), v),
                      )
                    }
                  />
                  {!isGroup ? (
                    <PeopleSelect
                      label="接收方"
                      value={str(rec.receiver)}
                      names={names}
                      disabled={disabled}
                      allowEmpty
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "receiver"), v),
                        )
                      }
                    />
                  ) : null}
                </div>
                {isGroup ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TextField
                      label="群聊 ID"
                      value={str(rec.group_id)}
                      disabled={disabled}
                      placeholder="riverside_plans"
                      onChange={(v) =>
                        onChangeRoot(
                          patchWhatsAppGroup(
                            root,
                            appPath,
                            index,
                            str(rec.group_id),
                            { group_id: v },
                          ),
                        )
                      }
                    />
                    <TextField
                      label="群聊名称"
                      value={groupName}
                      disabled={disabled}
                      placeholder="Riverside plans"
                      onChange={(v) =>
                        onChangeRoot(
                          patchWhatsAppGroup(
                            root,
                            appPath,
                            index,
                            str(rec.group_id),
                            { group_name: v, receiver: v },
                          ),
                        )
                      }
                    />
                    <div className="sm:col-span-2">
                      <MediaUrlField
                        label="群聊头像"
                        fieldKey="group_avatar_url"
                        value={str(rec.group_avatar_url)}
                        disabled={disabled}
                        mediaKind="image"
                        onChange={(groupAvatarUrl) =>
                          onChangeRoot(
                            patchWhatsAppGroup(
                              root,
                              appPath,
                              index,
                              str(rec.group_id),
                              { group_avatar_url: groupAvatarUrl },
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <WhatsAppGroupMembersEditor
                        value={rec.group_members}
                        names={names}
                        disabled={disabled}
                        onChange={(members) =>
                          onChangeRoot(
                            patchWhatsAppGroup(
                              root,
                              appPath,
                              index,
                              str(rec.group_id),
                              { group_members: members },
                            ),
                          )
                        }
                      />
                    </div>
                  </div>
                ) : null}
                <TextField
                  label="发送时间"
                  fieldKey="sent_at"
                  value={str(rec.sent_at)}
                  disabled={disabled}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "sent_at"), v),
                    )
                  }
                />
                <TextField
                  label={supportsImages ? "正文或图片说明" : "正文"}
                  value={str(rec.body)}
                  disabled={disabled}
                  multiline
                  rows={4}
                  onChange={(v) => {
                    const bodyPath = childPath(itemPath, "body");
                    onChangeRoot(
                      supportsImages && v.trim() === ""
                        ? deleteAtPath(root, bodyPath)
                        : setAtPath(root, bodyPath, v),
                    );
                  }}
                />
                {supportsImages ? (
                  <MediaUrlField
                    label="消息图片"
                    fieldKey="image_url"
                    value={str(rec.image_url)}
                    disabled={disabled}
                    mediaKind="image"
                    onChange={(v) => {
                      const imagePath = childPath(itemPath, "image_url");
                      onChangeRoot(
                        v.trim() === ""
                          ? deleteAtPath(root, imagePath)
                          : setAtPath(root, imagePath, v),
                      );
                    }}
                  />
                ) : null}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <EnumSelect
                      label="发送状态"
                      fieldKey="delivery"
                      value={str(rec.delivery) || "sent"}
                      options={enumOptionsAt(
                        `${appPath}[0].delivery`,
                      )}
                      disabled={disabled}
                      onChange={(v) => {
                        if (v === "sent") {
                          // omit when sent: delete key by rewriting without delivery
                          const nextRec = { ...rec };
                          delete nextRec.delivery;
                          onChangeRoot(setAtPath(root, itemPath, nextRec));
                        } else {
                          onChangeRoot(
                            setAtPath(
                              root,
                              childPath(itemPath, "delivery"),
                              v,
                            ),
                          );
                        }
                      }}
                    />
                  </div>
                  {str(rec.delivery) && str(rec.delivery) !== "sent" ? (
                    <Badge variant="warning">{str(rec.delivery)}</Badge>
                  ) : null}
                </div>
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}

function messageThreadKey(
  message: Record<string, unknown>,
  supportsGroups: boolean,
): string {
  const groupId = supportsGroups ? str(message.group_id).trim() : "";
  if (groupId) {
    const groupName = str(message.group_name).trim() || groupId;
    return `群聊：${groupName}::${groupId}`;
  }
  const key = threadKey(str(message.sender), str(message.receiver));
  return key ? `私聊：${key}` : "";
}

function WhatsAppGroupMembersEditor({
  value,
  names,
  disabled,
  onChange,
}: {
  value: unknown;
  names: string[];
  disabled?: boolean;
  onChange: (next: Record<string, unknown>[]) => void;
}) {
  const members = asArray(value).map((member) =>
    isRecord(member) ? member : {},
  );

  const updateMember = (
    index: number,
    field: string,
    nextValue: string,
  ) => {
    onChange(
      members.map((member, memberIndex) =>
        memberIndex === index
          ? { ...member, [field]: nextValue }
          : member,
      ),
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">群成员 ({members.length})</p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            const used = new Set(members.map((member) => str(member.name)));
            const name = names.find((candidate) => !used.has(candidate)) ?? "";
            onChange([
              ...members,
              { name, nickname: name, avatar_url: "" },
            ]);
          }}
        >
          添加成员
        </Button>
      </div>
      {members.map((member, index) => (
        <div
          key={`${index}-${str(member.name)}`}
          className="flex flex-col gap-3 rounded-md border border-border p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">成员 {index + 1}</p>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              disabled={disabled}
              className="text-destructive hover:text-destructive"
              onClick={() =>
                onChange(members.filter((_, memberIndex) => memberIndex !== index))
              }
            >
              删除
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <PeopleSelect
              label="人物"
              value={str(member.name)}
              names={names}
              disabled={disabled}
              allowEmpty
              onChange={(nextName) => updateMember(index, "name", nextName)}
            />
            <TextField
              label="群内昵称"
              value={str(member.nickname)}
              disabled={disabled}
              onChange={(nickname) =>
                updateMember(index, "nickname", nickname)
              }
            />
          </div>
          <MediaUrlField
            label="成员头像"
            fieldKey="avatar_url"
            value={str(member.avatar_url)}
            disabled={disabled}
            mediaKind="image"
            onChange={(avatarUrl) =>
              updateMember(index, "avatar_url", avatarUrl)
            }
          />
        </div>
      ))}
    </div>
  );
}

function threadLabel(key: string): string {
  return key.split("::", 1)[0] ?? key;
}

function patchWhatsAppGroup(
  root: unknown,
  appPath: string,
  currentIndex: number,
  currentGroupId: string,
  patch: Record<string, unknown>,
): unknown {
  const messages = asArray(getAtPath(root, appPath));
  const updated = messages.map((message, index) => {
    if (!isRecord(message)) return message;
    const sameGroup =
      currentGroupId.trim().length > 0 &&
      str(message.group_id).trim() === currentGroupId.trim();
    if (index !== currentIndex && !sameGroup) return message;
    return { ...message, ...patch };
  });
  return setAtPath(root, appPath, updated);
}
